<template>
  <div>
    <b-button
      :variant="variant || (hasStripeTerminal ? 'danger' : 'outline-danger')"
      block
      :disabled="busy || !canPayAdmin"
      v-b-modal.admin-payment-options-modal
    >
      {{ trans('admin_payment') }}
    </b-button>
    <b-modal
      id="admin-payment-options-modal"
      :title="trans('admin_options')"
      size="lg"
      :no-close-on-backdrop="busy"
      :no-enforce-focus="busy"
      :no-close-on-esc="busy"
      :hide-header-close="busy"
      hide-header-close
      ok-only
      :ok-title="trans('cancel')"
      ok-variant="outline-secondary"
      :ok-disabled="busy"
      @shown="onShown"
    >
      <b-row class="my-3">
        <b-col v-for="pw in payWithOptions" :key="'pay_with_'+pw.value" class="col-6">
          <b-button :variant="pw.variant || 'outline-danger'" block :disabled="disabled || busy || !canPayAdmin" @click="onClickPayWith(pw.value)" class="py-4 mb-2">
            <i v-if="!!pw.icon" :class="`fa fa-fw fa-${pw.icon}`"></i>
            <strong>{{ pw.text }}</strong>
          </b-button>
        </b-col>
      </b-row>

      <b-row class="my-3">
        <b-col v-if="hasCCFee">
          <b-form-group inline class="text-danger">
            <b-form-checkbox v-model="include_cc_fee">
                {{ trans('include') }} {{ convenienceFeeLabel }}
              </b-form-checkbox>
            </b-form-group>
        </b-col>
        <b-col>
          <b-row>
            <b-col v-if="invoice.admin_can_pay_later">
              <b-button variant="outline-danger" :disabled="disabled || busy" @click="onPayLater">
                {{ trans('pay_later') }}
              </b-button>
            </b-col>
            <b-col v-if="canSendShoppingCart" class="text-right">
              <b-button variant="outline-danger" :disabled="disabled || busy" v-b-modal.payment-send-cart-modal>
                <i class="fa fa-fw fa-envelope"></i>
                {{ trans('send_shopping_cart') }}
              </b-button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row class="my-3">
        <b-col>
          <pos-pay-amount
            v-if="!busy"
            :organization="organization"
            :invoice="invoice"
            :show-convenience-fee-preview="include_cc_fee"
            @convenience-fee-price="convenience_fee_price = $event"
            @adjusted-payment-amount="adjusted_payment_amount = $event"
          ></pos-pay-amount>
        </b-col>
        <b-col v-if="canShowGiftCard">
          <pos-gift-cards
            source="admin-options"
            ref="checkout_gift_cards"
            :organization="organization"
            class="mt-2 mb-4"
            :invoice="invoice"
            @invoice="onGiftCardUpdated"
            @busy="busy = $event"
            @error="error = $event"
          ></pos-gift-cards>
          <b-button v-if="canShowGiftCard" block variant="outline-branded" class="my-3" :disabled="busy" @click="onApplyGiftCard">
            {{ trans('apply') }}
          </b-button>
        </b-col>
      </b-row>

      <p v-if="busy">
        <i class="fa fa-fw fa-circle-notch fa-spin"></i>
        {{ trans('please_wait') }}
      </p>
      <p v-if="error" class="text-danger">
        {{ error }}
      </p>
    </b-modal>

    <send-message-modal
      v-if="canSendShoppingCart"
      id="payment-send-cart-modal"
      :organization="organization"
      :user="organization.auth_user"
      type="email"
      :subject="trans('shopping_cart')"
      :people="[invoice.recipient]"
      message-sample-id="shopping_cart"
      :ucid="invoice.ucid"
      @sent="onCartSent"
    ></send-message-modal>

    <stripe-terminal-payment-modal
      :organization="organization"
      :invoice="invoice"
      :amount="amount"
      :include-cc-fee="includeCcFee"
      :amount-with-cc-fee="adjusted_payment_amount"
      :stripe-id="invoice.cart_payment ? invoice.cart_payment.stripe_id : null"
      :client-secret="clientSecret"
      @client-secret="$emit('client-secret', $event)"
      @paid="onPaidWithStripeTerminal"
    ></stripe-terminal-payment-modal>
    <pos-cash-payment-modal
      :organization="organization"
      :invoice="invoice"
      :amount="adjusted_payment_amount"
      @paying="onPayingWithCash"
    ></pos-cash-payment-modal>
    <pos-pay-with-modal
      :organization="organization"
      :invoice="invoice"
      :amount="adjusted_payment_amount"
      :title="trans(pay_with)"
      @paying="onPayWith"
    ></pos-pay-with-modal>
  </div>
</template>

<script>
  import SendMessageModal from './../sendout/SendMessageModal'
  import StripeTerminalPaymentModal from './../payment/StripeTerminalPaymentModal'
  import PosCashPaymentModal from './../pos/PosCashPaymentModal'
  import PosPayWithModal from './../pos/PosPayWithModal'
  import PosPayAmount from './../pos/PosPayAmount'
  import PosGiftCards from './../pos/PosGiftCards'

  export default {
    props: {
      organization: Object,
      invoice: Object,
      disabled: Boolean,
      clientSecret: String,
      variant: String,
      isCart: {
        type: Boolean,
        default: false,
      },
      showConvenienceFeePreview: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      SendMessageModal,
      StripeTerminalPaymentModal,
      PosCashPaymentModal,
      PosPayWithModal,
      PosPayAmount,
      PosGiftCards,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        show: false,
        amount: this.invoice.total,
        pay_with: null,
        cash_amount: null,
        send_email: !this.invoice.is_no_client,
        include_cc_fee: this.showConvenienceFeePreview,
        convenience_fee_price: null,
        adjusted_payment_amount: null,
      }
    },
    computed: {
      canShowGiftCard() {
        if (this.invoice.applied_gift_card_amount > 0) return true
        return this.invoice.pay_now_amount > 0 &&
          this.organization.has_gift_cards &&
          !this.invoice.prevent_paying_with_gift_cards &&
          !this.invoice.has_gift_card_purchase
      },
      cashChange() {
        if (!this.cash_amount || this.cash_amount < this.amount) return null
        return numeral(this.cash_amount - this.amount).format('1,000.00')
      },
      isAmountValid() {
        const amount = parseFloat(this.amount)
        return !!amount &&
          !isNaN(amount) &&
          amount > 0 &&
          amount <= this.invoice.total
      },
      canPayAdmin() {
        return this.isAmountValid || !!this.invoice || !this.invoice.expired_at || !this.invoice.finalized_at
      },
      canSendShoppingCart() {
        return !!this.organization.auth_user &&
          !!this.invoice &&
          !this.invoice.is_no_client &&
          this.organization.auth_user.microsite_permissions.can_send_shopping_cart
      },
      hasStripeTerminal() {
        return this.organization.has_stripe &&
          this.organization.has_stripe_terminal
      },
      payWithOptions() {
        let options = []
        if (this.hasStripeTerminal) {
          options.push({value: 'stripe_terminal', text: trans('stripe_terminal'), variant: 'danger', icon: 'mobile-alt fa-rotate-180'})
        }
        options.push({value: 'cash', text: trans('cash'), icon: 'dollar-sign'})
        
        options.push({value: 'credit_card_offline', text: trans('credit_card_offline')})
        options.push({value: 'debit_offline', text: trans('debit_offline')})
        options.push({value: 'check', text: trans('check')})
        options.push({value: 'other', text: trans('other')})
        return options
      },
      serialized() {
        return {
          amount: this.pay_with == 'pay_later' ? 0 : this.amount, // excludes the CC fee -  will be calculated by the backend
          cash_amount: this.pay_with == 'cash' ? (this.cash_amount || 0)*1 : null,
          pay_with: this.pay_with,
          paid_at: moment().toISOString(),
          send_email: this.send_email,
        }
      },
      convenienceFeeLabel() {
        return this.organization.convenience_fee_label || this.trans('credit_card_convenience_fee')
      },
      hasCCFee() {
        if (this.organization.has_convenience_fee) return true
        else return false
      },
      includeCcFee() {
        return this.include_cc_fee && this.convenience_fee_price && this.convenience_fee_price > 0
      },
    },
    methods: {
      onShown() {
        this.include_cc_fee = this.showConvenienceFeePreview
        this.amount = this.invoice.total
        if (this.invoice.is_no_client) this.send_email = false
      },
      onCartSent() {

      },
      formatter(value) {
        if (value === null || value === undefined || value === '') return ''
        return numeral(value).format('0.00')
      },
      onApplyGiftCard() {
        this.$refs.checkout_gift_cards.onApplyGiftCard()
      },
      onGiftCardUpdated(invoice) {
        this.$emit('invoice', invoice)
        if (!!invoice.finalized_at) {
          this.$nextTick(() => {
            this.$emit('next')
            this.$bvModal.hide('admin-payment-options-modal')
          })
        }
      },
      onClickPayWith(pay_with) {
        this.pay_with = pay_with
        if (this.pay_with == 'cash') {
          this.$bvModal.show('pos-cash-payment-modal')
          return
        }
        else if (this.pay_with == 'stripe_terminal') {
          return this.onPayWithStripeTerminal()
        }
        this.$bvModal.show('pos-pay-with-modal')
        return
      },
      onPayingWithCash(cash_amount, send_email) {
        this.cash_amount = cash_amount
        this.send_email = send_email
        this.onPayAdmin()
      },
      onPayWith(send_email) {
        this.send_email = send_email
        this.onPayAdmin()
      },
      onPayWithStripeTerminal() {
        if (this.invoice.pay_now_amount == 0) return
        this.busy = true
        this.error = false
        let url = `/org/${this.organization.id}/cart/prepare_admin_stripe_terminal_payment`
        axios.post(url, {
          amount: this.amount, // excludes the CC fee -  will be calculated by the backend
          include_cc_fee: this.includeCcFee,
        })
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data.invoice)
          this.$emit('client-secret', response.data.stripe_info.client_secret)
          this.$bvModal.show('stripe-terminal-payment-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onPaidWithStripeTerminal(stripe_id) {
        this.send_email = !this.invoice.is_no_client
        this.onPayAdmin()
      },
      onPayAdmin() {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/pay_as_admin`
        if (this.includeCcFee) {
          url += `?include_cc_fee=true`
        }
        axios.post(url, this.serialized)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
          this.$nextTick(() => {
            this.$emit('next')
          })
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onPayLater() {
        this.pay_with = 'pay_later'
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/cart/pay_as_admin`
        let data = this.serialized
        axios.post(url, data)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
          this.$nextTick(() => {
            this.$emit('next')
          })
          this.$bvModal.hide('admin-payment-options-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.adminPaymentOptions = this
    },
  }
</script>